/**
 * Enhanced Product Recommender for SlowBoat AI Assistant
 * Specializes in Travel eSIM and travel product recommendations
 */

(function() {
    'use strict';

    // Product matching patterns
    const PRODUCT_PATTERNS = {
        esim: {
            keywords: ['esim', 'e-sim', 'sim', 'data', 'mobile', 'internet', 'roaming', 'connectivity', 'phone', 'cellular', 'network'],
            triggers: ['travel sim', 'data plan', 'mobile data', 'internet abroad', 'roaming', 'connectivity', 'phone plan'],
            priority: 10
        },
        tours: {
            keywords: ['tour', 'excursion', 'sightseeing', 'day trip', 'adventure', 'experience', 'activity'],
            triggers: ['bali tour', 'australia tour', 'day tour', 'sightseeing', 'adventure'],
            priority: 8
        },
        transfers: {
            keywords: ['transfer', 'airport', 'transport', 'pickup', 'drop-off', 'car', 'driver'],
            triggers: ['airport transfer', 'pickup', 'transport', 'car rental'],
            priority: 7
        },
        dining: {
            keywords: ['restaurant', 'dining', 'food', 'voucher', 'meal', 'cuisine'],
            triggers: ['restaurant', 'dining', 'food voucher', 'meal'],
            priority: 6
        }
    };

    // Enhanced product search and recommendation
    function enhanceProductRecommendations() {
        const sections = document.querySelectorAll('[data-section-id]');
        
        sections.forEach(section => {
            const sectionId = section.dataset.sectionId;
            const productsDataElement = document.getElementById(`products-data-${sectionId}`);
            
            if (!productsDataElement) return;

            let productsData;
            try {
                productsData = JSON.parse(productsDataElement.textContent);
            } catch (error) {
                console.error('Failed to parse products data:', error);
                return;
            }

            // Enhanced product search function
            function searchProducts(query, category = null) {
                const queryLower = query.toLowerCase();
                const matches = [];

                // Special handling for eSIM queries
                if (isESIMQuery(queryLower)) {
                    const esimProducts = findESIMProducts(productsData.products);
                    return esimProducts.map(product => ({
                        product: product,
                        score: 10,
                        category: 'esim',
                        reason: 'Travel eSIM product match'
                    }));
                }

                // Regular product search
                productsData.products.forEach(product => {
                    const score = calculateProductScore(product, queryLower, category);
                    if (score > 0) {
                        matches.push({
                            product: product,
                            score: score,
                            category: detectProductCategory(product),
                            reason: generateMatchReason(product, queryLower)
                        });
                    }
                });

                // Sort by score and return top matches
                return matches.sort((a, b) => b.score - a.score).slice(0, 5);
            }

            function isESIMQuery(query) {
                const esimTriggers = [
                    'esim', 'e-sim', 'sim card', 'data plan', 'mobile data', 
                    'internet abroad', 'roaming', 'connectivity', 'phone plan',
                    'data package', 'mobile internet', 'travel sim'
                ];
                
                return esimTriggers.some(trigger => query.includes(trigger));
            }

            function findESIMProducts(products) {
                return products.filter(product => {
                    const titleLower = product.title.toLowerCase();
                    const descLower = product.description.toLowerCase();
                    const tags = product.tags.map(tag => tag.toLowerCase());
                    
                    return titleLower.includes('esim') || 
                           titleLower.includes('sim') ||
                           descLower.includes('esim') ||
                           descLower.includes('data plan') ||
                           tags.some(tag => tag.includes('sim') || tag.includes('data') || tag.includes('mobile'));
                });
            }

            function calculateProductScore(product, query, category) {
                let score = 0;
                const titleLower = product.title.toLowerCase();
                const descLower = product.description.toLowerCase();
                const tags = product.tags.map(tag => tag.toLowerCase());
                const keywords = product.keywords || [];

                // Direct title match (highest priority)
                if (titleLower.includes(query)) score += 10;
                
                // Description match
                if (descLower.includes(query)) score += 5;
                
                // Tag matches
                tags.forEach(tag => {
                    if (tag.includes(query) || query.includes(tag)) score += 3;
                });

                // Keyword matches
                keywords.forEach(keyword => {
                    if (keyword.includes(query) || query.includes(keyword)) score += 2;
                });

                // Category bonus
                if (category && detectProductCategory(product) === category) {
                    score += 5;
                }

                // Availability bonus
                if (product.available) score += 1;

                return score;
            }

            function detectProductCategory(product) {
                const titleLower = product.title.toLowerCase();
                const typeLower = product.type.toLowerCase();
                const tags = product.tags.map(tag => tag.toLowerCase());

                if (titleLower.includes('esim') || titleLower.includes('sim') || 
                    tags.some(tag => tag.includes('sim') || tag.includes('data'))) {
                    return 'esim';
                }
                if (titleLower.includes('tour') || typeLower.includes('tour')) {
                    return 'tours';
                }
                if (titleLower.includes('transfer') || titleLower.includes('transport')) {
                    return 'transfers';
                }
                if (titleLower.includes('restaurant') || titleLower.includes('dining')) {
                    return 'dining';
                }
                return 'other';
            }

            function generateMatchReason(product, query) {
                const category = detectProductCategory(product);
                switch (category) {
                    case 'esim':
                        return 'Perfect for staying connected during your travels';
                    case 'tours':
                        return 'Exciting experience in your destination';
                    case 'transfers':
                        return 'Convenient transportation solution';
                    case 'dining':
                        return 'Delicious dining experience';
                    default:
                        return 'Recommended travel essential';
                }
            }

            function formatProductRecommendation(matches) {
                if (matches.length === 0) return null;

                const topMatch = matches[0];
                const product = topMatch.product;
                
                return {
                    type: 'product_recommendation',
                    category: topMatch.category,
                    product: {
                        id: product.id,
                        title: product.title,
                        description: product.description,
                        price: product.price,
                        currency: product.currency,
                        url: product.url,
                        image: product.featured_image,
                        reason: topMatch.reason,
                        available: product.available
                    },
                    alternatives: matches.slice(1, 3).map(match => ({
                        title: match.product.title,
                        price: match.product.price,
                        url: match.product.url
                    }))
                };
            }

            // Expose the search function globally for the AI chat
            window.SlowBoatProductSearch = {
                searchProducts: searchProducts,
                formatRecommendation: formatProductRecommendation,
                isESIMQuery: isESIMQuery,
                findESIMProducts: () => findESIMProducts(productsData.products)
            };

            console.log(`Product recommender initialized for section ${sectionId}`);
            console.log('Available eSIM products:', findESIMProducts(productsData.products).length);
        });
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', enhanceProductRecommendations);
    } else {
        enhanceProductRecommendations();
    }

    // Re-initialize on section updates
    document.addEventListener('shopify:section:load', enhanceProductRecommendations);
})();